import { useEffect, useState } from "react";
import { Card, Flex, Segmented } from "antd";
import {
  BooleanParam,
  NumberParam,
  StringParam,
  withDefault,
} from "use-query-params";

import { ERROR_TYPE, FIELD_KEYS } from "constants/ErrorFields";
import { FILTER_KEYS } from "constants/table";
import {
  DateRangeArrayParam,
  NumberArrayParam,
  SorterArrayParam,
  useQueryParamsWithProducts,
} from "functions/queryParamFuncs";
import { useProduct } from "hooks/useProduct";
import ErrorTable from "components/ErrorList/ErrorTable";
import ErrorTableFooter from "components/ErrorList/ErrorTableFooter";
import ErrorTableHeader from "components/ErrorList/ErrorTableHeader";
import useFetch from "hooks/useFetch";

const errorTypeOptions = [
  { label: "Active Errors", value: ERROR_TYPE.ACTIVE },
  { label: "Historical Errors", value: ERROR_TYPE.HISTORICAL },
];

const ErrorListPage = () => {
  const {
    loading,
    responseData: errorListResponseData,
    doFetch: getErrorList,
  } = useFetch("/unitErrors/getUnitErrorList", "GET", []);
  const { selectedProductIds } = useProduct();
  const [queryParams, setQueryParams] = useQueryParamsWithProducts(
    selectedProductIds,
    {
      [FIELD_KEYS.ERROR_CODE]: withDefault(NumberArrayParam, []),
      [FIELD_KEYS.ERROR_DATE_TIME]: withDefault(DateRangeArrayParam, []),
      [FIELD_KEYS.ERROR_TYPE]: withDefault(BooleanParam, true),
      [FIELD_KEYS.UNIT_START_DATE_TIME]: withDefault(DateRangeArrayParam, []),
      [FIELD_KEYS.RESOLVE_ACTION]: withDefault(NumberArrayParam, []),
      [FILTER_KEYS.SEARCH]: withDefault(StringParam, ""),
      [FILTER_KEYS.PAGE]: withDefault(NumberParam, 1),
      [FILTER_KEYS.PAGE_SIZE]: withDefault(NumberParam, 10),
      [FILTER_KEYS.SORTERS]: SorterArrayParam,
    }
  );

  const [filters, setFilters] = useState({
    ...queryParams,
    products: selectedProductIds,
  });

  const updateFilters = (newFilters) => {
    if (FIELD_KEYS.ERROR_TYPE in newFilters) {
      newFilters[FILTER_KEYS.PAGE] = 1;

      if (newFilters[FILTER_KEYS.ERROR_TYPE] !== ERROR_TYPE.HISTORICAL) {
        newFilters[FILTER_KEYS.RESOLVE_ACTION] = [];
      }
    }

    setFilters({
      ...filters,
      ...newFilters,
    });
  };
  if (selectedProductIds.toString() !== filters.products.toString()) {
    updateFilters({ products: selectedProductIds });
  }

  const isHistoricalView =
    filters[FIELD_KEYS.ERROR_TYPE] === ERROR_TYPE.HISTORICAL;
  const tableData = errorListResponseData.errors ?? [];
  const tableDataTotalCount = errorListResponseData.total_count ?? 0;

  const fetchErrorListData = () => {
    getErrorList({
      ...filters,
      [FIELD_KEYS.ITEM_START_DATE_TIME]: DateRangeArrayParam.encode(
        filters[FIELD_KEYS.ITEM_START_DATE_TIME]
      ),
      [FIELD_KEYS.ERROR_DATE_TIME]: DateRangeArrayParam.encode(
        filters[FIELD_KEYS.ERROR_DATE_TIME]
      ),
      [FILTER_KEYS.SORTERS]: SorterArrayParam.encodeApi(
        filters[FILTER_KEYS.SORTERS]
      ),
    });
  };

  useEffect(() => {
    setQueryParams(filters);
    fetchErrorListData();
  }, [filters]);

  return (
    <>
      <Segmented
        options={errorTypeOptions}
        value={filters[FIELD_KEYS.ERROR_TYPE]}
        disabled={loading}
        onChange={(val) => updateFilters({ [FIELD_KEYS.ERROR_TYPE]: val })}
        size="large"
      />
      <Card>
        <Flex vertical={true} gap="middle" wrap="wrap">
          <ErrorTableHeader
            isHistoricalView={isHistoricalView}
            filters={filters}
            tableDataTotalCount={tableDataTotalCount}
            loading={loading}
            onFiltersChange={updateFilters}
          />
          <ErrorTable
            isHistoricalView={isHistoricalView}
            dataSource={tableData}
            loading={loading}
            onRefresh={fetchErrorListData}
          />
          <ErrorTableFooter
            filters={filters}
            tableDataTotalCount={tableDataTotalCount}
            loading={loading}
            onFiltersChange={updateFilters}
          />
        </Flex>
      </Card>
    </>
  );
};

export default ErrorListPage;
